import React from 'react';
import { ImageIcon, AlertTriangle, FileImage, Ruler, Zap, EyeOff, PiggyBank } from 'lucide-react';

const ImagesPanel = ({ project }) => {
  const images = project?.technical_audit?.image_optimization || null;

  if (!images || !images.total_images) {
    return (
      <div className="bg-black/30 border border-gray-850 p-8 rounded-2xl text-center animate-in fade-in duration-300">
        <ImageIcon size={32} className="text-gray-700 mx-auto mb-3" />
        <p className="text-sm text-gray-500">
          No image asset analysis available. Re-run an audit to detect heavy images, WebP opportunities, and CLS risks.
        </p>
      </div>
    );
  }

  const stats = [
    { label: 'Images Crawled', value: images.total_images, icon: FileImage, color: 'text-blue-400' },
    { label: 'Oversized (>200KB)', value: images.oversized_count, icon: AlertTriangle, color: 'text-red-400' },
    { label: 'Legacy Formats (No WebP)', value: images.non_modern_count, icon: ImageIcon, color: 'text-amber-400' },
    { label: 'Missing Width/Height (CLS)', value: images.missing_dimensions_count, icon: Ruler, color: 'text-yellow-400' },
    { label: 'No Lazy Loading', value: images.without_lazy_count, icon: Zap, color: 'text-purple-400' },
    { label: 'Missing Alt Text', value: images.missing_alt_count, icon: EyeOff, color: 'text-pink-400' },
  ];

  return (
    <div className="flex flex-col gap-6 animate-in fade-in duration-300">

      {/* Savings banner */}
      <div className="bg-emerald-950/25 border border-emerald-500/25 rounded-2xl p-5 flex items-center gap-4">
        <div className="bg-emerald-600/15 border border-emerald-500/30 p-3 rounded-xl text-emerald-400 shrink-0">
          <PiggyBank size={22} />
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-[10px] text-emerald-300 uppercase tracking-widest font-bold">Estimated Weight Savings</span>
          <span className="text-2xl font-mono font-black text-emerald-300">~{images.potential_savings_kb || 0} KB</span>
          <span className="text-[11px] text-emerald-200/70">
            Conservative estimate from converting oversized rasters to WebP/AVIF (≈70% recompression).
          </span>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {stats.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-gray-950 border border-gray-800 ${color}`}>
              <Icon size={16} />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-mono font-black text-white">{value}</span>
              <span className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">{label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Heavy images list */}
      {images.heavy_images?.length > 0 && (
        <div className="flex flex-col gap-3">
          <h4 className="font-extrabold text-white text-sm flex items-center gap-2">
            <AlertTriangle size={15} className="text-red-400" />
            Heaviest Assets Detected (over {images.oversized_count > 1 ? 'sample' : ''} threshold)
          </h4>
          <div className="flex flex-col gap-2">
            {images.heavy_images.map((img, i) => (
              <div key={i} className="bg-gray-950/80 border border-gray-850 rounded-xl p-3 flex flex-col sm:flex-row justify-between sm:items-center gap-2">
                <div className="flex flex-col gap-0.5 truncate max-w-full sm:max-w-md">
                  <a href={img.src} target="_blank" rel="noreferrer" className="font-mono text-xs text-blue-400 hover:underline truncate">
                    {img.src}
                  </a>
                  <span className="text-[10px] text-gray-500 font-mono truncate">on {img.page_url}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] font-mono text-purple-300 bg-purple-950/50 border border-purple-500/25 px-2 py-0.5 rounded">
                    {img.format || 'unknown'}
                  </span>
                  <span className="text-[10px] font-mono font-bold text-red-300 bg-red-950/40 border border-red-500/25 px-2 py-0.5 rounded">
                    {img.size_kb} KB
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {images.recommendations?.length > 0 && (
        <div className="bg-black/30 border border-gray-850 rounded-2xl p-5 flex flex-col gap-3">
          <h4 className="font-extrabold text-white text-sm">Recommended Actions</h4>
          <div className="flex flex-col gap-2">
            {images.recommendations.map((rec, i) => (
              <div key={i} className="flex items-start gap-2.5 text-xs text-gray-300">
                <span className="w-4 h-4 rounded-full bg-blue-600/20 border border-blue-500/30 text-blue-400 flex items-center justify-center text-[9px] font-bold shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span className="leading-relaxed">{rec}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagesPanel;
